import React, { useContext, useMemo, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { AttachFile, CloudUpload, Download } from '@mui/icons-material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import BiteDBAPI from '../../../api/BiteDBAPI';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';
import { countries } from 'utils/countriesWithCodes';
import { productTemplateColumns } from '../../../api/bitedb/products/products.hooks';

interface IAddProductModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  database: any;
  handleRefresh?: any;
  parentId?: any;
}

export const AddProductModal: React.FC<IAddProductModalProps> = ({
  open,
  handleClose,
  title = 'Add Product',
  database,
  handleRefresh,
  parentId,
}) => {
  const [activeTab, setActiveTab] = useState<'manual' | 'bulk'>('manual');
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { cur_user } = useContext(GlobalContext);

  // Product fields
  const [productName, setProductName] = useState<string>('');
  const [productDescription, setProductDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [hierarchyType, setHierarchyType] = useState<string>('Assembly');
  const [htsCode, setHtsCode] = useState<string>('');
  const [manufacturer, setManufacturer] = useState<string>('');
  const [vendor, setVendor] = useState<string>('');
  const [modelNumber, setModelNumber] = useState<string>('');
  const [specifications, setSpecifications] = useState<string>('');
  const [countryOfOrigin, setCountryOfOrigin] = useState<string>('');
  const [status, setStatus] = useState<string>('Active');
  const [transMethod, setTransMethod] = useState<string>('');
  const [countryOfSmeltPour, setCountryOfSmeltPour] = useState<string>('');
  const [entryDate, setEntryDate] = useState<string>('');
  const [loadingDate, setLoadingDate] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [unitOfMeasure, setUnitOfMeasure] = useState<string>('');
  const [unitValue, setUnitValue] = useState<string>('');

  type COpt =
    | string
    | {
        name?: string;
        label?: string;
        countryName?: string;
        iso2?: string;
        alpha2?: string;
        alpha2Code?: string;
        usCustomsCountryCode?: string;
        country?: {
          name?: string;
          countryName?: string;
          iso2?: string;
          alpha2?: string;
          alpha2Code?: string;
        };
      };

  const getName = (o: COpt) => (typeof o === 'string' ? o : o?.name ?? '');
  const getIso = (o: COpt) =>
    typeof o === 'string' ? '' : o?.iso2 ?? ''?.toUpperCase();
  const displayLabel = (o: COpt) => {
    const n = getName(o);
    const code = getIso(o);
    return code ? `${n} - ${code}` : n;
  };

  const findCountryOption = (valueIsoOrName: string | undefined | null) => {
    if (!valueIsoOrName) {
      return null;
    }
    const v = String(valueIsoOrName).toUpperCase();
    return (
      (countries as COpt[]).find((c) => getIso(c)?.toUpperCase() === v) ||
      (countries as COpt[]).find((c) => getName(c).toUpperCase() === v) ||
      null
    );
  };

  const toDateOnly = (d: Date | null): Date | null => {
    if (!d) {
      return null;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const handleProductAddition = async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      const product = {
        name: productName,
        description: productDescription,
        category: category,
        hierarchy_type: hierarchyType,
        hts_code: htsCode,
        manufacturer: manufacturer,
        vendor: vendor,
        model_number: modelNumber,
        specifications: specifications,
        country_of_origin: countryOfOrigin,
        status_flag: status,
        trans_method: transMethod,
        country_of_smelt_pour: countryOfSmeltPour,
        entry_date: entryDate !== '' ? new Date(entryDate) : new Date(),
        loading_date: loadingDate !== '' ? new Date(loadingDate) : new Date(),
        quantity: quantity ? Number(quantity) : 0,
        unit_of_measure: unitOfMeasure,
        product_value: unitValue ? Number(unitValue) : 0,
        created_at: new Date(),
        updated_at: new Date(),
        database_id: database.id,
        parent: parentId,
      };

      // Use the createProduct endpoint
      const response = await BiteDBAPI.createProduct(
        product,
        cur_user.selected_team?.id,
      );

      if (response?.success) {
        enqueueSnackbar('Product added successfully', { variant: 'success' });
        handleClose();
        handleRefresh();
      } else {
        enqueueSnackbar('Error adding product', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Error adding product', { variant: 'error' });
      console.error('Error adding product:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (isProcessing) {
      return;
    }
    const accptFile = acceptedFiles[0];
    if (!accptFile) {
      return;
    }

    setIsProcessing(true);

    // Convert to standard File
    const fileNew = new File([accptFile], accptFile.name, {
      type: accptFile.type,
    });
    setFile(fileNew);

    try {
      await fileHandler(fileNew);
      enqueueSnackbar('File ready for submission!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error processing file. Please ensure it is a valid CSV or XLSX.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setFile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const fileHandler = async (fileToHandle: any) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          fileToHandle.type === 'text/csv' ||
          fileToHandle.name.endsWith('.csv') ||
          fileToHandle.name.endsWith('.xlsx')
        ) {
          resolve({
            name: fileToHandle.name,
            size: fileToHandle.size,
            type: fileToHandle.type,
          });
        } else {
          console.error(
            'Mock fileHandler error: Unsupported file type:',
            fileToHandle.type,
            fileToHandle.name,
          );
          reject(
            new Error(
              'Mock fileHandler: Unsupported file type. Please upload CSV or XLSX.',
            ),
          );
        }
      }, 500);
    });
  };

  const exportToCSV = (filename: string, data: any, headers: any) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach((item: any) => {
      const values = headers.map((header: any) => {
        const escaped = `${item[header]}`.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      enqueueSnackbar(
        'Browser does not support link.download attribute for mock CSV export.',
        { variant: 'error' },
      );
    }
  };

  const fileValidator = (currentFile: File) => {
    if (currentFile.size > 10 * 1024 * 1024) {
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const acceptedExtensions = ['.csv', '.xlsx'];

    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV or Excel (XLSX) file.',
        { variant: 'error' },
      );
      return {
        code: 'file-type-invalid',
        message: 'File type must be one of: .csv, .xlsx',
      };
    }
    return null;
  };

  const handleDeleteFile = () => {
    setFile(null);
    enqueueSnackbar('File removed.', { variant: 'info' });
  };

  const templateHeaders = useMemo(() => productTemplateColumns, []);

  const handleDownloadTemplate = () => {
    exportToCSV('product_template.csv', [], templateHeaders);
    enqueueSnackbar('Template download initiated.', { variant: 'info' });
  };

  const handleBulkUpload = async () => {
    if (!file) {
      enqueueSnackbar('Please upload a file first', { variant: 'error' });
      return;
    }

    if (isProcessing) {
      enqueueSnackbar('File is currently being processed. Please wait.', {
        variant: 'warning',
      });
      return;
    }

    try {
      setIsProcessing(true);

      await fileHandler(file);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('database_id', database.id);
      formData.append('created_by', cur_user.basics?.id || '');

      // Use the bulk products upload endpoint
      const response = await BiteDBAPI.bulkUploadProducts(formData);

      if (response?.success) {
        enqueueSnackbar('Bulk upload started successfully', {
          variant: 'success',
        });
        handleClose();
        handleRefresh();
      } else {
        enqueueSnackbar(`Bulk upload failed. ${response?.result}.`, {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error during bulk upload:', error);
      enqueueSnackbar('Unexpected error during bulk upload', {
        variant: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: ['.csv', '.xlsx'],
    multiple: false,
  });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  return (
    <>
      <Dialog
        className='dialog_box'
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
            maxWidth: '800px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          id='product-modal-title'
          className='modal-header-content modal-title'
        >
          {title}
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div className='screening-tabs-container'>
            <div className='screening-tabs-inner'>
              <div
                className={`screening-tab-btn${
                  activeTab === 'manual' ? '-selected' : ''
                }`}
                onClick={() => setActiveTab('manual')}
              >
                <div
                  className={`screening-tab-btn-text${
                    activeTab === 'manual' ? '-selected' : ''
                  }`}
                >
                  <AttachFile
                    style={{
                      marginRight: '5px',
                      color:
                        activeTab === 'manual' ? '#4895EF' : 'var(--card-text)',
                    }}
                    fontSize='small'
                  />
                  Manual entry
                </div>
              </div>
              <div
                className={`screening-tab-btn${
                  activeTab === 'bulk' ? '-selected' : ''
                }`}
                onClick={() => setActiveTab('bulk')}
              >
                <div
                  className={`screening-tab-btn-text${
                    activeTab === 'bulk' ? '-selected' : ''
                  }`}
                >
                  <CloudUpload
                    style={{
                      marginRight: '5px',
                      color:
                        activeTab === 'bulk' ? '#4895EF' : 'var(--card-text)',
                    }}
                    fontSize='small'
                  />
                  Bulk entry
                </div>
              </div>
            </div>
          </div>
          <div>
            {activeTab === 'manual' && (
              <div className='audit-add-modal-cont-body-frame'>
                <div className='audit-add-modal-cont-body-frame-form'>
                  <div className='audit-add-modal-head-cont-supporting'>
                    <h1>Enter product information manually</h1>
                  </div>
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='grid-layout' style={{ width: '100%' }}>
                      <div>
                        <label
                          htmlFor='product-name'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Name <span className='required-star'>*</span>
                        </label>
                        <input
                          type='text'
                          id='product-name'
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          placeholder='Product name'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                        />
                      </div>
                      {/* HTS Code*/}
                      <div>
                        <label
                          htmlFor='product-name'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          HS Code <span className='required-star'>*</span>
                        </label>
                        <input
                          type='text'
                          id='product-name'
                          value={htsCode}
                          onChange={(e) => setHtsCode(e.target.value)}
                          placeholder='HS Code'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                        />
                      </div>
                      {/* Category */}
                      <div>
                        <label
                          htmlFor='product-category'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Category
                        </label>
                        <select
                          id='product-category'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          <option value=''>Select category</option>
                          <option value='Electronics'>Electronics</option>
                          <option value='Processors'>Processors</option>
                          <option value='Memory'>Memory</option>
                          <option value='Storage'>Storage</option>
                          <option value='Graphics'>Graphics</option>
                          <option value='Power'>Power</option>
                          <option value='Audio'>Audio</option>
                          <option value='Video'>Video</option>
                          <option value='Mobile'>Mobile</option>
                          <option value='Networking'>Networking</option>
                          <option value='Peripherals'>Peripherals</option>
                          <option value='Software'>Software</option>
                          <option value='Accessories'>Accessories</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor='product-hierarchy-type'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Hierarchy Type
                        </label>
                        <select
                          id='product-hierarchy-type'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={hierarchyType}
                          onChange={(e) => setHierarchyType(e.target.value)}
                        >
                          <option value=''>Select hierarchy type</option>
                          <option value='Assembly'>Assembly</option>
                          <option value='Sub-Assembly'>Sub-Assembly</option>
                          <option value='Component'>Component</option>
                        </select>
                      </div>
                      <div className='col-span-2'>
                        <label
                          htmlFor='product-description'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Description
                        </label>
                        <textarea
                          id='product-description'
                          rows={3}
                          placeholder='Brief description'
                          value={productDescription}
                          onChange={(e) =>
                            setProductDescription(e.target.value)
                          }
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                        ></textarea>
                      </div>
                      <div>
                        <label
                          htmlFor='manufacturer'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Manufacturer
                        </label>
                        <input
                          type='text'
                          id='manufacturer'
                          placeholder='Manufacturer name'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={manufacturer}
                          onChange={(e) => setManufacturer(e.target.value)}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='vendor'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Vendor
                        </label>
                        <input
                          type='text'
                          id='vendor'
                          placeholder='Vendor name'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={vendor}
                          onChange={(e) => setVendor(e.target.value)}
                        />
                      </div>

                      {/* Model Number */}
                      <div>
                        <label
                          htmlFor='model-number'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Model Number
                        </label>
                        <input
                          type='text'
                          id='model-number'
                          placeholder='Model number'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={modelNumber}
                          onChange={(e) => setModelNumber(e.target.value)}
                        />
                      </div>
                      {/* Specifications */}
                      <div className='col-span-2'>
                        <label
                          htmlFor='specifications'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Specifications
                        </label>
                        <textarea
                          id='specifications'
                          rows={3}
                          placeholder='Technical specifications'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={specifications}
                          onChange={(e) => setSpecifications(e.target.value)}
                        />
                      </div>
                      {/* Country of Origin */}
                      <div>
                        <label
                          htmlFor='country-of-origin'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Country of Origin
                        </label>
                        <Autocomplete
                          options={countries as COpt[]}
                          getOptionLabel={(option) => displayLabel(option)}
                          isOptionEqualToValue={(opt, val) =>
                            (Boolean(getIso(opt)) &&
                              getIso(opt) === getIso(val)) ||
                            getName(opt) === getName(val)
                          }
                          componentsProps={{
                            popper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  color: 'var(--text-primary-white) !important',
                                },
                              }, // Higher z-index to sit above sidebar/modals
                            },
                            paper: {
                              sx: {
                                color: 'var(--text-primary-white) !important',
                              },
                            },
                          }}
                          sx={{
                            '::placeholder': { opacity: 1 },
                            background:
                              'var(--Background-200, var(--bg-primary))',
                            borderRadius: '8px',
                            height: '45px',
                            '& .MuiAutocomplete-input': {
                              color:
                                'var(--Text-Main, var(--input-text-color))',
                              fontFamily: 'var(--font-family)',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              border: 'none',
                              background:
                                'var(--Background-200, var(--bg-primary))',
                              borderRadius: '8px',
                              height: '45px',
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              border:
                                '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                              height: '45px',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor:
                                  'var(--Colors-Border-border-primary, var(--bg-border-db))',
                              },
                              color: 'var(--input-text-color)',
                              '&.Mui-focused': {
                                color: 'var(--focused-text-color)',
                              },
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--focused-text-color)',
                            },
                          }}
                          onChange={(_, newValue) =>
                            setCountryOfOrigin(getIso(newValue as COpt))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder='Select Country of Smelt'
                            />
                          )}
                          value={findCountryOption(countryOfOrigin || '')}
                        />
                      </div>
                      {/* Status */}
                      <div>
                        <label
                          htmlFor='status'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Status
                        </label>
                        <select
                          id='status'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value='new'>New</option>
                          <option value='not_reviewed'>Not Reviewed</option>
                          <option value='cleared'>Cleared</option>
                          <option value='quarantined'>Quarantined</option>
                          <option value='barred'>Barred</option>
                        </select>
                      </div>
                      {/* Transaction Method */}
                      <div>
                        <label
                          htmlFor='trans-method'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Transaction Method
                        </label>
                        <input
                          type='text'
                          id='trans-method'
                          placeholder='Transaction method'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={transMethod}
                          onChange={(e) => setTransMethod(e.target.value)}
                        />
                      </div>
                      {/* Country of Smelt Pour */}
                      <div>
                        <label
                          htmlFor='country-of-smelt'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Country of Smelt Pour
                        </label>

                        <Autocomplete
                          options={countries as COpt[]}
                          getOptionLabel={(option) => displayLabel(option)}
                          isOptionEqualToValue={(opt, val) =>
                            (Boolean(getIso(opt)) &&
                              getIso(opt) === getIso(val)) ||
                            getName(opt) === getName(val)
                          }
                          componentsProps={{
                            popper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  color: 'var(--text-primary-white) !important',
                                },
                              }, // Higher z-index to sit above sidebar/modals
                            },
                            paper: {
                              sx: {
                                color: 'var(--text-primary-white) !important',
                              },
                            },
                          }}
                          sx={{
                            '::placeholder': { opacity: 1 },
                            background:
                              'var(--Background-200, var(--bg-primary))',
                            borderRadius: '8px',
                            height: '45px',
                            '& .MuiAutocomplete-input': {
                              color:
                                'var(--Text-Main, var(--input-text-color))',
                              fontFamily: 'var(--font-family)',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              border: 'none',
                              background:
                                'var(--Background-200, var(--bg-primary))',
                              borderRadius: '8px',
                              height: '45px',
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              border:
                                '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                              height: '45px',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor:
                                  'var(--Colors-Border-border-primary, var(--bg-border-db))',
                              },
                              color: 'var(--input-text-color)',
                              '&.Mui-focused': {
                                color: 'var(--focused-text-color)',
                              },
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--focused-text-color)',
                            },
                          }}
                          onChange={(_, newValue) =>
                            setCountryOfSmeltPour(getIso(newValue as COpt))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder='Select Country of Smelt'
                            />
                          )}
                          value={findCountryOption(countryOfSmeltPour || '')}
                        />
                      </div>
                      {/* Entry Date */}
                      <div>
                        <label
                          htmlFor='entry-date'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Entry Date
                        </label>
                        <DatePicker<Date>
                          // Remove disabled prop
                          value={new Date(entryDate)}
                          onChange={(newValue: Date | null) => {
                            const ed = toDateOnly(newValue);
                            if (!ed) {
                              setEntryDate('');
                              return;
                            }
                            if (!loadingDate || loadingDate === '') {
                              setLoadingDate(ed.toString());
                            }
                            setEntryDate(ed.toString());
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor:
                                  'var(--Colors-Border-border-primary, var(--bg-border-db))',
                              },
                          }}
                          slotProps={{
                            actionBar: { actions: ['today'] },
                            textField: {
                              placeholder: 'Select Entry Date',
                              size: 'small',
                              fullWidth: true,
                              sx: {
                                height: '45px',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'var(--bg-primary);',
                                  borderRadius: '8px',
                                  height: '45px',
                                  paddingRight: '8px',
                                  color: 'var(--input-text-color)',
                                  display: 'flex',
                                  alignSelf: 'stretch',
                                  border:
                                    '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                                  background: 'var(--bg-primary);',
                                  boxShadow:
                                    '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                                  fontFamily: 'Inter',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: '500',
                                  lineHeight: '20px',
                                },
                                '& input': {
                                  color: 'var(--input-text-color)',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--secondary-color)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--input-placeholder-color)',
                                },
                                '& .MuiOutlinedInput-root.Mui-error:hover fieldset':
                                  {
                                    borderColor:
                                      'var(--input-placeholder-color)',
                                  },
                                '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset':
                                  {
                                    borderColor:
                                      'var(--input-placeholder-color)',
                                  },
                                '& .MuiFormLabel-root': {
                                  color: 'var(--focused-text-color)',
                                  fontSize: '14px',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'var(--focused-text-color)',
                                },
                              },
                            },
                          }}
                        />
                      </div>
                      {/* Loading Date */}
                      <div>
                        <label
                          htmlFor='loading-date'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Loading Date
                        </label>
                        <DatePicker<Date>
                          // Remove disabled prop
                          value={new Date(loadingDate)}
                          onChange={(newValue: Date | null) => {
                            const ed = toDateOnly(newValue);
                            if (!ed) {
                              setLoadingDate('');
                              return;
                            }
                            setLoadingDate(ed.toString());
                          }}
                          sx={{
                            '& fieldset .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor:
                                  'var(--Colors-Border-border-primary, var(--bg-border-db))',
                              },
                          }}
                          slotProps={{
                            actionBar: { actions: ['today'] },
                            textField: {
                              placeholder: 'Select Entry Date',
                              size: 'small',
                              fullWidth: true,
                              sx: {
                                height: '45px',
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'var(--bg-primary);',
                                  borderRadius: '8px',
                                  height: '45px',
                                  paddingRight: '8px',
                                  color: 'var(--input-text-color)',
                                  display: 'flex',
                                  alignSelf: 'stretch',
                                  border:
                                    '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
                                  background: 'var(--bg-primary);',
                                  boxShadow:
                                    '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                                  fontFamily: 'Inter',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: '500',
                                  lineHeight: '20px',
                                },
                                '& input': {
                                  color: 'var(--input-text-color)',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--secondary-color)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--input-placeholder-color)',
                                },
                                '& .MuiOutlinedInput-root.Mui-error:hover fieldset':
                                  {
                                    borderColor:
                                      'var(--input-placeholder-color)',
                                  },
                                '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset':
                                  {
                                    borderColor:
                                      'var(--input-placeholder-color)',
                                  },
                                '& .MuiFormLabel-root': {
                                  color: 'var(--focused-text-color)',
                                  fontSize: '14px',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'var(--focused-text-color)',
                                },
                              },
                            },
                          }}
                        />
                      </div>
                      {/* Quantity */}
                      <div>
                        <label
                          htmlFor='country-of-origin'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Quantity
                        </label>
                        <input
                          type='number'
                          id='quantity'
                          placeholder='Quantity'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={quantity}
                          onChange={(e) => setQuantity(e.target.value)}
                        />
                      </div>
                      {/* Unit Of Measure */}
                      <div>
                        <label
                          htmlFor='unit-of-measure'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Unit Of Measure
                        </label>
                        <input
                          type='text'
                          id='unit-of-measure'
                          placeholder='Unit of Measure'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={unitOfMeasure}
                          onChange={(e) => setUnitOfMeasure(e.target.value)}
                        />
                      </div>
                      {/* Unit Value */}
                      <div>
                        <label
                          htmlFor='unit-value'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Unit/Product Value
                        </label>
                        <input
                          type='number'
                          id='unit-value'
                          placeholder='unit value'
                          className='audit-add-modal-cont-body-frame-form-input'
                          style={{ width: '100%' }}
                          value={unitValue}
                          onChange={(e) => setUnitValue(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='audit-modal-cont-footer'>
                  <div className='audit-modal-cont-footer-content'>
                    <div
                      className='audit-modal-cont-footer-content-btn-cancel'
                      onClick={handleClose}
                    >
                      <div className='audit-modal-cont-footer-content-btn-text'>
                        Cancel
                      </div>
                    </div>
                    <div
                      className='audit-modal-cont-footer-content-btn-upload'
                      onClick={!isSaving ? handleProductAddition : undefined}
                    >
                      <div
                        className={`audit-modal-cont-footer-content-btn-text ${
                          isSaving ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        style={{ color: '#FFF' }}
                      >
                        {isSaving ? (
                          <span className='flex items-center justify-center'>
                            <CircularProgress
                              style={{
                                color: '#FFF',
                                marginTop: '5px',
                                width: '30px',
                                height: '30px',
                              }}
                            />
                            Saving...
                          </span>
                        ) : (
                          'Save'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'bulk' && (
              <div
                className='audit-add-modal-cont-body'
                style={{ paddingTop: '12px', paddingBottom: '12px' }}
              >
                <div className='audit-add-modal-cont-body-frame'>
                  <div className='audit-add-modal-cont-body-frame-form'>
                    <div className='audit-add-modal-head-cont-supporting'>
                      <h1>Upload multiple products using a CSV file</h1>
                    </div>
                    <div className='modal-grid'>
                      <div className='modal-section'>
                        <h3 className='modal-section-title'>
                          1. Download Template
                        </h3>
                        <button
                          onClick={handleDownloadTemplate}
                          className='button button-primary button-full-width'
                        >
                          <Download style={{ marginRight: '8px' }} />
                          Download Template
                        </button>
                        <p className='modal-section-subtitle'>
                          Download a .csv template file with example headers.
                        </p>
                      </div>
                      <div className='modal-section'>
                        <h3 className='modal-section-title'>2. Upload File</h3>
                        <div {...getRootProps()} className={dropzoneClassName}>
                          <input {...getInputProps()} />
                          {file ? (
                            <div className='dropzone-content-item'>
                              <svg
                                className='dropzone-icon-success'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth={1.5}
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                                />
                              </svg>
                              <p className='dropzone-filename'>{file.name}</p>
                              <p className='dropzone-filesize'>
                                {(file.size / 1024).toFixed(2)} KB
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile();
                                }}
                                className='dropzone-remove-button'
                              >
                                Remove File
                              </button>
                            </div>
                          ) : isProcessing ? (
                            <div className='dropzone-content-item'>
                              Processing file...
                            </div>
                          ) : (
                            <div className='dropzone-content-item'>
                              <p className='dropzone-text'>
                                <span className='dropzone-text-highlight'>
                                  Click to upload
                                </span>{' '}
                                or drag and drop
                              </p>
                              <p className='dropzone-subtext'>
                                CSV or XLSX (max 10MB)
                              </p>
                              {isDragReject && (
                                <p className='dropzone-error-text'>
                                  Invalid file type or size.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Upload Button */}
                  <div className='audit-modal-cont-footer'>
                    <div className='audit-modal-cont-footer-content'>
                      <div
                        className='audit-modal-cont-footer-content-btn-cancel'
                        onClick={handleClose}
                      >
                        <div className='audit-modal-cont-footer-content-btn-text'>
                          Cancel
                        </div>
                      </div>
                      <div className='audit-modal-cont-footer-content-btn-upload'>
                        <div
                          className={`audit-modal-cont-footer-content-btn-text ${
                            isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={!isProcessing ? handleBulkUpload : undefined}
                          style={{ color: '#FFF' }}
                        >
                          {isProcessing ? 'Uploading...' : 'Upload'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddProductModal;
